import { cn } from 'lib/utils'
import * as React from 'react'
import { Eyebrow } from './Eyebrow'

interface EmptyStateProps {
  /** Optional decorative icon (lucide). Sized 24px by default. */
  icon?: React.ReactNode
  /** Mono uppercase label above the title. Optional. */
  eyebrow?: React.ReactNode
  /** The empty-state headline. Sentence case, period at the end. */
  title: React.ReactNode
  /** Body paragraph explaining the empty state and what to do. */
  lede?: React.ReactNode
  /** Primary CTA — typically `Button variant="t3-primary" size="t3-md"`. */
  primaryAction?: React.ReactNode
  /** Optional secondary action — typically `Button variant="t3-tertiary"` or a `link-muted`. */
  secondaryAction?: React.ReactNode
  /** Layout. `centered` = vertically centered in a fill region; `inline` = top-aligned in a card or container. */
  layout?: 'centered' | 'inline'
  className?: string
}

/**
 * Canonical empty state for T3 surfaces. Pattern: optional icon, eyebrow,
 * title, lede, then a primary acid CTA (and optional secondary action).
 *
 * Voice: never blame the user. "Nothing here yet." reads better than "You
 * haven't created anything." Pair with an inviting CTA, never an order.
 *
 * Spec: playbook/design-system/components.md#empty-state.
 * Voice: playbook/design-system/voice.md#tone-by-context (see "Empty states").
 *
 * Examples:
 *
 *   <EmptyState
 *     icon={<Calendar size={24} />}
 *     title="Your week is clear."
 *     lede="Set up a weekly plan and Taka will draft your posts."
 *     primaryAction={<Button variant="t3-primary" size="t3-md">Set up my plan</Button>}
 *   />
 *
 *   <EmptyState layout="inline" title="No drafts yet." />
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  eyebrow,
  title,
  lede,
  primaryAction,
  secondaryAction,
  layout = 'centered',
  className,
}) => {
  return (
    <div
      className={cn(
        'flex flex-col gap-4 text-center',
        layout === 'centered'
          ? 'mx-auto h-full max-w-[440px] items-center justify-center px-6 py-16'
          : 'items-start text-left',
        className
      )}
    >
      {icon && (
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-[12px] border border-line bg-bg-2 text-paper-dim">
          {icon}
        </div>
      )}
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h2 className="text-[20px] leading-[1.2] font-semibold tracking-[-0.01em] text-paper sm:text-[22px]">
        {title}
      </h2>
      {lede && <p className="lede !text-[14px]">{lede}</p>}
      {(primaryAction || secondaryAction) && (
        <div
          className={cn(
            'flex flex-wrap items-center gap-3 pt-2',
            layout === 'centered' && 'justify-center'
          )}
        >
          {primaryAction}
          {secondaryAction}
        </div>
      )}
    </div>
  )
}
